import type { ColDef, IRowNode, ValueGetterParams } from 'ag-grid-community'
import _ from 'lodash'
import { useCallback, useMemo } from 'react'

import type {
  Group,
  RowGroupingConfig,
  RowWithGroupingMetadata,
} from '@/components/lemmala-design-system/table/types'
import { extractColumnValue } from '@/components/lemmala-design-system/table/utils/column-utils'
import { buildGroupedRows } from '@/components/lemmala-design-system/table/utils/group-rows'
import { reorderGroupableRows } from '@/components/lemmala-design-system/table/utils/reorder-groupable-rows'

type UseGroupableRowsOptions<T extends { id: string }> = {
  data: T[]
  /** Converts flat rows into groups (domain-specific logic). */
  toGroups: (data: T[]) => Group<T>[]
  expandedGroups: Set<string>
  enabled?: boolean
}

/**
 * Manages collapsible row grouping for AG Grid tables.
 *
 * Takes a flat list of rows and a caller-provided `groupFn`, then produces
 * processed data with parent / header / child markers, expand/collapse state,
 * row styling, and a `groupingConfig` compatible with `AgDataTable`.
 */
export function useGroupableRows<T extends { id: string }>({
  data,
  toGroups,
  expandedGroups,
  enabled = true,
}: UseGroupableRowsOptions<T>) {
  const groups = useMemo(() => (enabled ? toGroups(data) : []), [data, enabled, toGroups])

  const processedData = useMemo<RowWithGroupingMetadata<T>[]>(() => {
    if (!enabled) return data
    return buildGroupedRows(groups, expandedGroups)
  }, [data, groups, expandedGroups, enabled])

  const groupsWithChildren = useMemo(() => groups.filter((g) => g.children.length > 0), [groups])

  // childrenByGroupId: for result counting of collapsed groups.
  // rowsByGroupId: for group-aware filtering and search (parent + children).
  const { childrenByGroupId, rowsByGroupId } = useMemo(() => {
    // The parent id is the group id.
    const byGroupId = _.keyBy(groupsWithChildren, 'parent.id')
    return {
      childrenByGroupId: _.mapValues(byGroupId, 'children'),
      rowsByGroupId: _.mapValues(byGroupId, (g) => [g.parent, ...g.children]),
    }
  }, [groupsWithChildren])

  const groupingConfig = useMemo<RowGroupingConfig<RowWithGroupingMetadata<T>>>(
    () => ({
      isGroupHeader: (row) => !!row.isGroupHeader,
      isExpanded: (row) => !!row.isExpanded,
      getChildren: (row) => childrenByGroupId[row.groupId ?? ''] ?? [],
      doesChildMatchSearch: () => true,
      doesChildMatchFilter: () => true,
    }),
    [childrenByGroupId],
  )

  const wrapColumnDefs = useCallback(
    (columnDefs: ColDef<RowWithGroupingMetadata<T>>[]): ColDef<RowWithGroupingMetadata<T>>[] => {
      if (!enabled) return columnDefs

      return columnDefs.map((col) => {
        if (col.filter === false) return col
        if (!col.filterValueGetter && !col.valueGetter && !col.field) return col

        return {
          ...col,
          filterValueGetter: (params: ValueGetterParams<RowWithGroupingMetadata<T>>) => {
            const row = params.data
            if (!row) return undefined

            const members = row.groupId ? rowsByGroupId[row.groupId] : undefined
            if (!members) return extractColumnValue(col, row, params)

            return extractColumnValue(col, members[0] as RowWithGroupingMetadata<T>, params)
          },
        }
      })
    },
    [enabled, rowsByGroupId],
  )

  const wrapSearchMatch = useCallback(
    (doesRowMatch: (row: RowWithGroupingMetadata<T>, query: string) => boolean) => {
      return (row: RowWithGroupingMetadata<T>, query: string): boolean => {
        if (!enabled || !row.groupId || !(row.groupId in rowsByGroupId))
          return doesRowMatch(row, query)

        return rowsByGroupId[row.groupId].some((member) => doesRowMatch(member, query))
      }
    },
    [enabled, rowsByGroupId],
  )

  const postSortRows = useCallback(
    ({ nodes }: { nodes: IRowNode<RowWithGroupingMetadata<T>>[] }) => {
      const dataToNode = new Map(nodes.filter((n) => n.data).map((n) => [n.data!, n]))
      const reordered = reorderGroupableRows([...dataToNode.keys()])
      nodes.length = 0
      nodes.push(...reordered.map((d) => dataToNode.get(d)!))
    },
    [],
  )

  return {
    processedData,
    wrapColumnDefs,
    wrapSearchMatch,
    postSortRows,
    groupingConfig,
  }
}
